import { useEffect, useState } from "react";
import type { ConciergeResource } from "@/lib/concierge/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, FileText } from "lucide-react";
import { emitConciergeEvent } from "@/lib/concierge/events";
import { getConciergeMediaSignedUrl } from "@/lib/admin/concierge-media";


function toEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.hostname.includes("vimeo.com") && /^\/\d+/.test(u.pathname)) {
      return `https://player.vimeo.com/video${u.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
}

function useSignedUrl(path: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    if (!path) {
      setUrl(null);
      return;
    }
    getConciergeMediaSignedUrl(path).then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [path]);
  return url;
}

function VideoCard({ r, siteId }: { r: ConciergeResource & { storage_path?: string | null }; siteId?: string }) {
  const signed = useSignedUrl(r.storage_path);
  const hasUpload = !!r.storage_path;
  const hasEmbed = !hasUpload && !!r.url;
  if (!hasUpload && !hasEmbed) return null;

  return (
    <Card className="concierge-card overflow-hidden group">
      <div
        className="relative w-full bg-black/40"
        style={{ paddingTop: "56.25%" }}
        onClick={() => siteId && emitConciergeEvent(siteId, "resource_view", { resource_id: r.id, metadata: { type: "video" } })}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/40 to-transparent z-10" />
        {hasUpload ? (
          signed ? (
            <video
              key={signed}
              src={signed}
              controls
              preload="metadata"
              className="absolute inset-0 h-full w-full"
              onPlay={() => siteId && emitConciergeEvent(siteId, "resource_view", { resource_id: r.id, metadata: { type: "video" } })}
              onEnded={() => siteId && emitConciergeEvent(siteId, "resource_complete", { resource_id: r.id, metadata: { type: "video" } })}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
              Loading…
            </div>
          )
        ) : (
          <iframe
            src={toEmbedUrl(r.url as string)}
            title={r.title ?? "Video"}
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
      {(r.title || r.description) && (
        <CardContent className="pt-4 space-y-1">
          {r.title && <p className="font-medium tracking-tight">{r.title}</p>}
          {r.description && <p className="text-sm text-muted-foreground">{r.description}</p>}
        </CardContent>
      )}
    </Card>
  );
}

function PodcastCard({ r, siteId }: { r: ConciergeResource; siteId?: string }) {
  if (!r.url) return null;
  return (
    <Card className="concierge-card">
      <CardHeader>
        <CardTitle className="text-base font-heading tracking-tight">{r.title ?? "Podcast"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {r.description && <p className="text-sm text-muted-foreground">{r.description}</p>}
        <audio
          controls
          src={r.url}
          className="w-full"
          onPlay={() => siteId && emitConciergeEvent(siteId, "resource_view", { resource_id: r.id, metadata: { type: "podcast" } })}
          onEnded={() => siteId && emitConciergeEvent(siteId, "resource_complete", { resource_id: r.id, metadata: { type: "podcast" } })}
        />
      </CardContent>
    </Card>
  );
}

function ReportCard({ r, siteId }: { r: ConciergeResource; siteId?: string }) {
  if (!r.url) return null;
  return (
    <Card className="concierge-card">
      <CardContent className="pt-6 flex items-start gap-4">
        <div className="concierge-icon-badge shrink-0">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <a
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium tracking-tight hover:underline"
            onClick={() => siteId && emitConciergeEvent(siteId, "resource_view", { resource_id: r.id, metadata: { type: "report" } })}
          >
            {r.title ?? "Report"}
          </a>
          {r.description && (
            <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function LinkCard({ r, siteId }: { r: ConciergeResource; siteId?: string }) {
  if (!r.url) return null;
  return (
    <Card className="concierge-card">
      <CardContent className="pt-6 flex items-start gap-4">
        <div className="concierge-icon-badge shrink-0">
          <ExternalLink className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <a
            href={r.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium tracking-tight hover:underline"
            onClick={() => siteId && emitConciergeEvent(siteId, "resource_view", { resource_id: r.id, metadata: { type: "link" } })}
          >
            {r.title ?? r.url}
          </a>
          {r.description && (
            <p className="text-sm text-muted-foreground mt-1">{r.description}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const LABELS: Record<string, string> = {
  video: "Videos",
  podcast: "Podcasts",
  report: "Reports",
  link: "Links",
};

const EYEBROWS: Record<string, string> = {
  video: "Watch",
  podcast: "Listen",
  report: "Read",
  link: "Explore",
};

export function ResourceSection({
  type,
  resources,
  siteId,
}: {
  type: string;
  resources: ConciergeResource[];
  siteId?: string;
}) {
  if (resources.length === 0) return null;
  const label = LABELS[type] ?? type;
  const eyebrow = EYEBROWS[type] ?? "";
  return (
    <section className="space-y-5">
      <div className="concierge-hairline pt-5 flex items-baseline justify-between gap-4">
        <div className="space-y-1">
          {eyebrow && (
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {eyebrow}
            </p>
          )}
          <h2 className="text-2xl font-heading font-medium tracking-tight">{label}</h2>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">
          {resources.length}
        </span>
      </div>
      <div className={type === "video" ? "grid gap-5 md:grid-cols-2" : "grid gap-3"}>
        {resources.map((r) => {
          if (type === "video") return <VideoCard key={r.id} r={r} siteId={siteId} />;
          if (type === "podcast") return <PodcastCard key={r.id} r={r} siteId={siteId} />;
          if (type === "report") return <ReportCard key={r.id} r={r} siteId={siteId} />;
          return <LinkCard key={r.id} r={r} siteId={siteId} />;
        })}
      </div>
    </section>
  );
}

export default ResourceSection;
